
import { IonContent, IonItem, IonLabel, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

// Question interface
interface QuestionData {
    question: string;
    correct: boolean;
}


const TrueFalseSenseOrgan: React.FC = () => {
    const [questions, setQuestions] = useState<QuestionData[]>([]);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {
        try {
            let data: QuestionData[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {
                const response = await fetch(process.env.REACT_APP_API_URL + 'TrueFalseSenseOrgan.json');
                data = await response.json();
            } else {
                const module = await import('../../../datas/TrueFalseSenseOrgan.json');
                data = module.default;
            }

            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
        } catch (error) {
            console.error('Error loading data:', error);
        }
    };

    useEffect(() => {
        // Simulating an API fetch to load questions
        fetchQuestions();
        document.title = "Sense Organs True or False Worksheet for UKG | General Awareness"; // Set page title
    }, []);

    const shuffleArray = (array: any[]) => {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    };

    const handleOptionClick = (selectedMyOption: boolean) => {
        console.log(selectedMyOption)
        const currentQuestion = questions[currentIndex];
        setSelectedOption(selectedMyOption);
        if (selectedMyOption === currentQuestion.correct) {
            setError(false);
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                }
                setShowImage(false);
                setError(false);
                setSelectedOption(null)
            }, 5000); // Shortened delay to 3 seconds
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Test UKG kids’ knowledge of the five sense organs with a simple true or false worksheet. Download printable practice sheets to build early science skills." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/sense-organ/true-false" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart seasons additionWord" style={{ marginTop: "0%" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Sense Organs</h1>

                                    <p>
                                        Want to check how well your child or student knows the five sense organs? Try this fun and simple <strong>true or false worksheet</strong> designed for UKG students. These statements will help young learners review their understanding of the body’s five senses.
                                    </p>

                                    <h2>👁️👂 What Are Sense Organs?</h2>
                                    <p>
                                        The human body has five main sense organs:
                                    </p>
                                    <ul>
                                        <li>👁️ <strong>Eyes</strong> – to see</li>
                                        <li>👂 <strong>Ears</strong> – to hear</li>
                                        <li>👃 <strong>Nose</strong> – to smell</li>
                                        <li>👅 <strong>Tongue</strong> – to taste</li>
                                        <li>✋ <strong>Skin</strong> – to feel</li>
                                    </ul>

                                    <h2>✅ Sample True or False Statements</h2>
                                    <ul>
                                        <li>We see with our eyes. – <strong>True</strong></li>
                                        <li>The tongue helps us hear. – <strong>False</strong></li>
                                        <li>The nose is used for smelling. – <strong>True</strong></li>
                                        <li>Skin is used to see. – <strong>False</strong></li>
                                        <li>Ears help us listen to sounds. – <strong>True</strong></li>
                                    </ul>

                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Sense Organs</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-home"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My Home</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/general-awareness/my-school"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>My School</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🎯 What Your Child Will Learn</h2>
                                    <ul>
                                        <li>Identify and name the five senses</li>
                                        <li>Connect each organ with its function</li>
                                        <li>Develop basic science and reading skills</li>
                                    </ul>

                                    <h2>👨‍👩‍👧 Tips for Teaching</h2>
                                    <ul>
                                        <li>Let children act out the senses while reading the statements.</li>
                                        <li>Use real objects like a bell or flower for demonstrations.</li>
                                        <li>Encourage group discussion about why a statement is true or false.</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Sense Organs</h1>

                                <p>
                                    Want to check how well your child or student knows the five sense organs? Try this fun and simple <strong>true or false worksheet</strong> designed for UKG students. These statements will help young learners review their understanding of the body’s five senses.
                                </p>

                                <h2>👁️👂 What Are Sense Organs?</h2>
                                <p>
                                    The human body has five main sense organs:
                                </p>
                                <ul>
                                    <li>👁️ <strong>Eyes</strong> – to see</li>
                                    <li>👂 <strong>Ears</strong> – to hear</li>
                                    <li>👃 <strong>Nose</strong> – to smell</li>
                                    <li>👅 <strong>Tongue</strong> – to taste</li>
                                    <li>✋ <strong>Skin</strong> – to feel</li>
                                </ul>

                                <h2>✅ Sample True or False Statements</h2>
                                <ul>
                                    <li>We see with our eyes. – <strong>True</strong></li>
                                    <li>The tongue helps us hear. – <strong>False</strong></li>
                                    <li>The nose is used for smelling. – <strong>True</strong></li>
                                    <li>Skin is used to see. – <strong>False</strong></li>
                                    <li>Ears help us listen to sounds. – <strong>True</strong></li>
                                </ul>

                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Sense Organs</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-home"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My Home</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/general-awareness/my-school"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>My School</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🎯 What Your Child Will Learn</h2>
                                <ul>
                                    <li>Identify and name the five senses</li>
                                    <li>Connect each organ with its function</li>
                                    <li>Develop basic science and reading skills</li>
                                </ul>

                                <h2>👨‍👩‍👧 Tips for Teaching</h2>
                                <ul>
                                    <li>Let children act out the senses while reading the statements.</li>
                                    <li>Use real objects like a bell or flower for demonstrations.</li>
                                    <li>Encourage group discussion about why a statement is true or false.</li>
                                </ul>
                            </article>
                            <div className="mt-2 mb-2">
                                <h3 >
                                    {questions[currentIndex]?.question}
                                </h3>
                            </div>

                            <IonRadioGroup onIonChange={(e) => handleOptionClick(e.detail.value!)}>
                                <IonItem
                                    className={`grid-item ${selectedOption === true
                                        ? true === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={true} />
                                    <IonLabel>True</IonLabel>
                                </IonItem>
                                <IonItem
                                    className={`grid-item ${selectedOption === false
                                        ? false === questions[currentIndex]?.correct
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}>
                                    <IonRadio slot="start" value={false} />
                                    <IonLabel>False</IonLabel>
                                </IonItem>
                            </IonRadioGroup>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default TrueFalseSenseOrgan;
